import { useRef, useState } from 'react';
import {
  IReturnShipments,
  setReturnShipments,
  useReturnStore,
} from '@src/stores/returnStore';
import { setRefundValues } from '@src/stores/summaryStore';
import {
  fetchRefundEstimationAPI,
  getIdentifier,
  handleAprissCall,
  handleMultiReturnCreateCall,
  TIMEOUT_ERROR_CODE,
} from '@src/utils/api-helpers';
import {
  emitReturnsFlowStep,
  emitReturnsOutcome,
} from '@src/utils/returns-instrumentation-context';
import {
  buildAprissFinalizeRequest,
  buildRefundEstimationRequest,
  buildSsReturnCreateRequest,
} from '@src/utils';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
} from '@src/constants/general';
import { CreateReturnOrderData } from '@src/models/api';
import { RefundValues } from '@src/models/returns';
import { CallInformation } from '@src/hooks/useMultiCallCreateReturn';
import { useNavigateToReturnSummary } from '@src/hooks/useNavigateToReturnSummary';

const DUPLICATE_ERROR_CODE = 'COM_RETURN_RECORD_ALREADY_EXISTS_ERROR';
const DROPOFF = RETURN_METHOD_TO_SHIPMENT_MAP[RETURN_METHOD.UPS_DROP_OFF];

export type KeepItSubmissionStatus = 'idle' | 'submitting' | 'timeout';

/**
 * Orchestrates a single-call Keep It return submission for UPS drop-off:
 * createReturn → Appriss finalize → navigate to the Keep It success summary,
 * skipping the standard Number-of-Boxes, Refund-Method, and Finalize steps.
 *
 * Keep It is server-decided (Policy Validator `isKeepIt`).
 *
 * Outcomes:
 * - success / duplicate (`COM_RETURN_RECORD_ALREADY_EXISTS_ERROR`) →
 *   Appriss finalize (success only) + navigate to the Keep It success summary.
 * - timeout (`/ssrreturncreate` aborts) → `status: 'timeout'`; the customer must
 *   explicitly retry from the timeout screen. We genuinely don't know whether the
 *   return was created, so we never fall back.
 * - any other (non-timeout) failure on the INITIAL submit → caller's `onFallback`
 *   routes the customer back into the standard UPS flow.
 *
 * On RETRY, only success/duplicate navigate; every other outcome (timeout again
 * OR a hard failure) stays on the timeout screen — once a timeout has occurred the
 * return's true state is unknown, so falling back is unsafe.
 */
const useKeepItCreateReturn = () => {
  const navigateToReturnSummary = useNavigateToReturnSummary();
  const [status, setStatus] = useState<KeepItSubmissionStatus>('idle');
  const [isRetrying, setIsRetrying] = useState(false);
  // True once the customer has clicked "Try Again" at least once — set when the
  // retry STARTS, so on its own it does not mean the retry failed (it's true
  // mid-flight too). The timeout screen infers "retry already failed" by combining
  // this with `!isRetrying`: success/duplicate navigate away, so a settled retry
  // that's still on the screen must have failed. Drives the updated banner copy and
  // the "Return to Order Details" exit button.
  const [hasRetried, setHasRetried] = useState(false);

  // Caches the refund estimation across the initial submit and retries of a single
  // Keep It attempt: the inputs (order + selected items) don't change between
  // attempts, so once we have a value there's no need to re-fetch on every retry.
  // Holds undefined until a fetch succeeds, so a failed/empty estimate still gets
  // re-attempted on the next retry (the summary hides the breakdown when undefined).
  const refundValuesRef = useRef<RefundValues | undefined>(undefined);

  /**
   * Core submission body, shared by the initial submit and retries. Reads fresh
   * store state at call time: the Return Method step updates the store
   * (PolicyValidator response, shipments, selected items) and invokes this in the
   * SAME synchronous tick, so the hook's render-time closures would be stale.
   * `getState()` reflects the just-applied updates (and lets retries resubmit
   * without re-running PolicyValidator).
   *
   * `onHardFailure` handles a non-timeout, non-duplicate error — fall back on the
   * initial submit, stay put on retry.
   */
  const runSubmission = async (onHardFailure: () => void) => {
    const {
      orderDetailsV2,
      isMilitaryExchangeGuest,
      policyValidatorResponse,
      returnShipments,
      selectedItems,
      fraudDecisions,
    } = useReturnStore.getState();

    if (!orderDetailsV2 || !policyValidatorResponse) {
      onHardFailure();
      return;
    }

    // The Number-of-Boxes step is skipped for Keep It, so default DROPOFF
    // shipments to a single box (mirrors useInitializeBoxQuantities).
    const shipmentsForSubmit: IReturnShipments[] = (returnShipments || []).map(
      (shipment) =>
        shipment.shipmentType === DROPOFF
          ? { ...shipment, boxQuantity: shipment.boxQuantity ?? 1 }
          : shipment,
    );
    setReturnShipments(shipmentsForSubmit);

    // Refund-Method step is skipped too; fetch the estimation in parallel so the
    // summary breakdown is populated. If unavailable, the breakdown is hidden.
    // Reuse a cached value across retries (see refundValuesRef) to avoid re-hitting
    // the endpoint on every "Try Again"; only fetch when we don't yet have one.
    const refundPromise: Promise<RefundValues | undefined> =
      refundValuesRef.current
        ? Promise.resolve(refundValuesRef.current)
        : fetchRefundEstimationAPI(
            buildRefundEstimationRequest(orderDetailsV2, selectedItems),
          ).then((refund) => {
            refundValuesRef.current = refund;
            return refund;
          });

    const request = buildSsReturnCreateRequest({
      orderDetailsV2,
      isGuest: isMilitaryExchangeGuest,
      policyValidatorET: policyValidatorResponse.et || '',
      deliveryDictionary: orderDetailsV2.deliveryDictionary,
      returnShipments: shipmentsForSubmit,
      selectedItems,
      shipmentType: DROPOFF,
      orderVersion: orderDetailsV2.versionId || '',
      isKeepIt: policyValidatorResponse.isKeepIt,
    });

    const { data, error } = await handleMultiReturnCreateCall(request);

    if (error) {
      if (error.code === DUPLICATE_ERROR_CODE) {
        // A return already exists for this order: treat as success, but with no
        // return data we show the barebones (TBD) summary and skip Appriss.
        const duplicateCall: CallInformation = {
          type: RETURN_METHOD.UPS_DROP_OFF,
          success: true,
          data: {} as CreateReturnOrderData,
        };
        setRefundValues(await refundPromise);
        navigateToReturnSummary([duplicateCall], true, true);
        return;
      }
      if (error.code === TIMEOUT_ERROR_CODE) {
        // We don't know whether the return was created — show the retry screen.
        setStatus('timeout');
        return;
      }
      onHardFailure();
      return;
    }

    if (data) {
      handleAprissCall(
        buildAprissFinalizeRequest({
          orderDetailsV2,
          returnOrderId: data.returnOrderId,
          orderDateForReturn: data.orderDateForReturn,
          policyValidatorET: policyValidatorResponse.et || '',
          shipmentType: DROPOFF,
          identifier: getIdentifier(fraudDecisions, DROPOFF) || '0',
          isGuest: isMilitaryExchangeGuest,
        }),
      );
      setRefundValues(await refundPromise);
      navigateToReturnSummary(
        [{ type: RETURN_METHOD.UPS_DROP_OFF, success: true, data }],
        false,
        true,
      );
    }
  };

  const submitKeepItReturn = async (onFallback: () => void) => {
    setStatus('submitting');
    // New Relic: the customer entered the Keep-It path (keep item + refund).
    emitReturnsFlowStep('keep_it_selected');
    try {
      await runSubmission(() => {
        // Non-timeout failure on the initial submit: fall back to standard UPS.
        setStatus('idle');
        onFallback();
      });
    } catch {
      // Unexpected throw (e.g. builder or navigation error): release the button
      // AND fall back to the standard flow, same as a hard failure. Resetting only
      // the hook status here would leave the caller's `isContinuing` flag stuck
      // `true`, permanently loading the Continue button on the method screen.
      //
      // A caught exception is never reported to New Relic as a JavaScriptError, so
      // without this emit the whole submission — including a skipped Apriss finalize —
      // fails completely silently. Fixed literal only; the error itself is not
      // attached, as its message can carry response or order content.
      emitReturnsOutcome('keep_it_create', 'error', {
        reason: 'unhandled_exception',
      });
      setStatus('idle');
      onFallback();
    }
  };

  const retryKeepItSubmit = async () => {
    setIsRetrying(true);
    setHasRetried(true);
    // New Relic: customer re-submitted after an ambiguous create timeout.
    emitReturnsOutcome('keep_it_create', 'timeout', { retryAttempted: true });
    try {
      // Hard failures on retry keep the customer on the timeout/retry screen; only
      // success/duplicate (handled inside runSubmission) navigate away.
      await runSubmission(() => setStatus('timeout'));
    } finally {
      setIsRetrying(false);
    }
  };

  return {
    submitKeepItReturn,
    retryKeepItSubmit,
    status,
    isSubmitting: status === 'submitting',
    isRetrying,
    hasRetried,
  };
};

export default useKeepItCreateReturn;
